import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Settings } from "lucide-react";
import { useUser } from "@/hooks/useUser";
import { getCurrentUserOrganisation } from "@/utils/roleUtils";

export interface ForecastBudgetVarianceTooltipContentProps {
  onBudgetMax?: number;
  moderateBudgetMax?: number;
  organisationId?: string;
  onClose?: () => void;
}

export const ForecastBudgetVarianceTooltipContent: React.FC<ForecastBudgetVarianceTooltipContentProps> = ({
  onBudgetMax: propOnBudgetMax,
  moderateBudgetMax: propModerateBudgetMax,
  organisationId: propOrgId,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const userOrg = getCurrentUserOrganisation(user);

  const onBudgetMax = propOnBudgetMax ?? userOrg?.organisation?.onBudgetMax ?? 5;
  const moderateBudgetMax = propModerateBudgetMax ?? userOrg?.organisation?.moderateBudgetMax ?? 20;
  const orgId = propOrgId ?? userOrg?.organisationId ?? "";

  return (
    <div className="p-3 text-slate-800 max-w-sm text-left normal-case space-y-3 font-normal">
      <div>
        <div className="font-bold text-sm text-slate-900">
          {t("forecast_budget_variance")}
        </div>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          {t("forecast_budget_variance_desc")}
        </p>
      </div>

      <div className="space-y-2 text-xs">
        {/* Row 1: Under budget */}
        <div className="flex items-center justify-between gap-4">
          <span className="font-bold text-slate-700">
            {t("variance_under_zero")}
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#EBF3FF] text-[#2563EB] border border-[#BFDBFE]/60 whitespace-nowrap">
            {t("under_budget")}
          </span>
        </div>

        {/* Row 2: On budget */}
        <div className="flex items-center justify-between gap-4">
          <span className="font-bold text-slate-700">
            {t("variance_range_on_budget", { onBudgetMax })}
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0]/60 whitespace-nowrap">
            {t("on_budget")}
          </span>
        </div>

        {/* Row 3: Moderately over budget */}
        <div className="flex items-center justify-between gap-4">
          <span className="font-bold text-slate-700">
            {t("variance_range_moderate", { onBudgetMax, moderateBudgetMax })}
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FEF3C7]/60 text-[#D97706] border border-[#FDE68A]/80 whitespace-nowrap">
            {t("MODERATE_BUDGET")}
          </span>
        </div>

        {/* Row 4: Significantly over budget */}
        <div className="flex items-center justify-between gap-4">
          <span className="font-bold text-slate-700">
            {t("variance_over_moderate", { moderateBudgetMax })}
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FEE2E2]/70 text-[#DC2626] border border-[#FECACA]/80 whitespace-nowrap">
            {t("SIGNIFICANT_BUDGET")}
          </span>
        </div>
      </div>

      {/* Remaining budget explanation */}
      <div className="pt-2 border-t border-slate-100 space-y-0.5 text-xs">
        <div className="font-bold text-slate-900 text-[11px]">
          {t("remaining_budget_title") || t("Remaining budget")}
        </div>
        <p className="text-[11px] text-slate-600 leading-snug">
          {t("remaining_budget_desc") || t("Amount by which the Forecast Final Cost is below the Initial Budget.")}
        </p>
      </div>

      {/* Footer link to KPI Settings */}
      <div className="pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-slate-500">
        <Settings className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span>
          {t("thresholds_configured_in")}{" "}
          {orgId ? (
            <Link
              to={`/organisation/${orgId}/kpi-settings`}
              className="text-[#535EF9] hover:underline font-medium"
            >
              {t("kpi_settings")}
            </Link>
          ) : (
            <span className="text-[#535EF9] font-medium">{t("kpi_settings")}</span>
          )}
          .
        </span>
      </div>
    </div>
  );
};
